import { useCopyToClipboard } from '@hooks/useCopyToClipboard';
import { Icon } from '@components/Icon/Icon';
import { Tooltip } from '@components/Tooltip/Tooltip';
import { ActionButtonContainer } from '../Actions';
import type { BaseActionProps } from '../Actions';
import { useStrings } from '@hooks/useStrings';
import type { Placement } from 'tippy.js';

export interface CopyActionProps extends BaseActionProps {
  /**
   * The text that is copied when the copy button is pressed.
   * If it is a function, it executes when the copy button is pressed.
   */
  toCopy: string | (() => string);

  /**
   * Callback when the copy button is pressed.
   */
  onCopy?: () => void;

  /**
   * Text to show in the action tooltip.
   * Defaults to 'Copy'.
   */
  actionText?: string;

  /**
   * Message to show in the tooltip when the text is copied.
   * Defaults to 'Copied!'.
   */
  actionFeedback?: string;

  /**
   * Define rule for preferred tooltip placement.
   */
  placement?: Placement;
}

/**
 * CopyAction component
 *
 * {@link https://storybook.hotjar.com/?path=/docs/components-copyaction--copy-action Code documentation}
 *
 * @example
 * <CopyAction toCopy='Text to copy' description='Copy my text'/>
 */
export const CopyAction = ({
  toCopy,
  onCopy,
  description,
  actionText,
  actionFeedback,
  className,
  placement,
}: CopyActionProps): JSX.Element => {
  const { isCopied, copy } = useCopyToClipboard(toCopy, onCopy);
  const { actions } = useStrings();

  const finalActionText = actionText || actions.copy;
  const finalActionFeedback = actionFeedback || actions.copied;

  return (
    <Tooltip
      text={isCopied ? finalActionFeedback : finalActionText}
      hideOnClick={false}
      placement={placement}
    >
      <ActionButtonContainer className={className} onClick={copy} type="button">
        <Icon
          size="m"
          icon={isCopied ? 'check_alt' : 'copy'}
          description={description}
        />
      </ActionButtonContainer>
    </Tooltip>
  );
};
